import Box from '@material-ui/core/Box'
import Radio from '@material-ui/core/Radio'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import MyLocationIcon from '@material-ui/icons/MyLocation'
import React, { useContext, useEffect, useRef, useState } from 'react'

import colorContext from '../../../../../../Context/colorContext'
import { getGeolocationByLatLng } from '../../../../../../services/geoLocation'

interface Geolocation {
  latitude: number
  longitude: number
}

interface MapComponentProps {
  geolocation?: Geolocation
  setCepNumber?: (cep: string) => void
  searchLocalization?: (shouldSearch: boolean) => void
  cepNumber?: string
  precision?: boolean
  setPrecision?: (precision: boolean | ((prev: boolean) => boolean)) => void
  setGeolocation?: (geolocation: Geolocation) => void
  setLocal?: (local: any) => void
  setCity?: (city: any) => void
  setNeighborhood?: (neighborhood: any) => void
}

interface LeafletMapDivElement extends HTMLDivElement {
  _leaflet_map?: any
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    border: '2px solid #DAE1EF',
    borderRadius: 8,
    padding: '16px 20px',
    cursor: 'pointer',
    transition: 'all 0.3s ease-in-out',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      borderColor: '#C5D3E8',
    },
  },
  cardChecked: {
    borderColor: theme.palette.primary.main,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  cardInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardIcon: {
    width: 22,
    height: 22,
    color: '#9CA3AF',
  },
  cardIconChecked: {
    color: theme.palette.primary.main,
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  mapContainerHidden: {
    height: 0,
    overflow: 'hidden',
    marginTop: 0,
  },
  mapContainerVisible: {
    height: 'auto',
    overflow: 'hidden',
    marginTop: 12,
    borderRadius: 8,
  },
}))

const MapComponent: React.FC<MapComponentProps> = ({
  geolocation,
  setCepNumber,
  searchLocalization,
  cepNumber,
  precision,
  setPrecision,
  setGeolocation,
  setLocal,
  setCity,
  setNeighborhood,
}) => {
  const { colors } = useContext(colorContext)
  const classes = useStyles()
  const mapRef = useRef<LeafletMapDivElement | null>(null)
  const mapInstanceRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const [mapReady, setMapReady] = useState(false) // eslint-disable-line @typescript-eslint/no-unused-vars

  const getCEP = async (latitude: number, longitude: number) => {
    try {
      const { data } = await getGeolocationByLatLng(latitude, longitude)
      if (data) {
        setCepNumber(data.address.postcode)
        setCity(data.address.city)
        setNeighborhood(data.address.suburb)
        setLocal(data.address.road)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const addMarkerAndCenterMap = (lat: number, lng: number, map: any) => {
    if (markerRef.current) {
      map.removeLayer(markerRef.current)
    }

    markerRef.current = (window as any).L.marker([lat, lng]).addTo(map)
    getCEP(lat, lng)
    map.setView([lat, lng], 16)
  }

  // Initialize map only once
  useEffect(() => {
    const L = (window as any).L
    if (!L || !mapRef.current || mapInstanceRef.current) return

    const map = L.map(mapRef.current).setView(
      [geolocation.latitude || -12.9714, geolocation.longitude || -38.5014],
      16,
    )

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)

    const bounds = [
      [-16.0, -39.0],
      [-11.0, -37.0],
    ]
    map.setMaxBounds(bounds)
    map.on('drag', () => map.panInsideBounds(bounds, { animate: false }))

    map.on('click', (e: any) => {
      const { lat, lng } = e.latlng
      setGeolocation({ latitude: lat, longitude: lng })
      addMarkerAndCenterMap(lat, lng, map)
    })

    mapInstanceRef.current = map
    setMapReady(true)

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  // When precision toggles on, tell Leaflet to recheck size (instant, no delay needed)
  useEffect(() => {
    if (precision && mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize()
    }
  }, [precision])

  // Update marker when geolocation changes externally (e.g. CEP search)
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map || !geolocation.latitude || !geolocation.longitude) return
    addMarkerAndCenterMap(geolocation.latitude, geolocation.longitude, map)
  }, [geolocation.latitude, geolocation.longitude])

  useEffect(() => {
    if (cepNumber) {
      searchLocalization(false)
    }
  }, [cepNumber])

  return (
    <Box className={classes.container}>
      <Box
        className={`${classes.card} ${precision ? classes.cardChecked : ''}`}
        onClick={() => setPrecision((prevState) => !prevState)}
      >
        <Box className={classes.cardHeader}>
          <Box className={classes.cardInfo}>
            <MyLocationIcon
              className={`${classes.cardIcon} ${
                precision ? classes.cardIconChecked : ''
              }`}
            />
            <Typography className={classes.cardTitle}>
              Selecionar local exato no mapa
            </Typography>
          </Box>
          <Radio
            color="primary"
            checked={precision}
            style={{ color: precision ? colors.accentColor : undefined }}
            inputProps={{ 'aria-label': 'Selecionar local exato no mapa' }}
            disableRipple
          />
        </Box>
        <Box
          className={
            precision ? classes.mapContainerVisible : classes.mapContainerHidden
          }
          onClick={(e) => e.stopPropagation()}
        >
          <div ref={mapRef} style={{ height: '250px', width: '100%' }} />
        </Box>
      </Box>
    </Box>
  )
}

export default MapComponent
